import { type Currency, formatAmount, liveBreakStorefrontPath } from "@loot-os/api-contract";
import type {
  StaffLiveBreakDetailWire,
  StaffLiveBreakEntryWire,
  StaffLiveBreakWire,
} from "@loot-os/functions/src/dashboard-api/trpc/routers/liveBreakWire.js";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@loot-os/ui/components/alert-dialog";
import { Badge } from "@loot-os/ui/components/badge";
import { BoxImage } from "@loot-os/ui/components/box-image";
import { Button, buttonVariants } from "@loot-os/ui/components/button";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@loot-os/ui/components/card";
import { DataTable } from "@loot-os/ui/components/data-table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@loot-os/ui/components/select";
import { Skeleton } from "@loot-os/ui/components/skeleton";
import { toast } from "@loot-os/ui/components/sonner";
import { createDataTableColumns } from "@loot-os/ui/lib/dataTable";
import { formatNumber } from "@loot-os/ui/lib/format";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { ExternalLink } from "lucide-react";
import { useMemo, useState } from "react";

import { ErrorAlert } from "../components/ErrorAlert";
import { Fact, FactGroup, FactList } from "../components/Facts";
import { LiveBreakFigures } from "../components/LiveBreakFigures";
import { LiveBreakSettingsFields } from "../components/LiveBreakSettingsFields";
import { LoadMoreButton } from "../components/LoadMoreButton";
import { ShortDate } from "../components/ShortDate";
import { UnsavedChangesDialog } from "../components/UnsavedChangesDialog";
import { amountColumnHeader, amountInputValue, useCurrency } from "../lib/currency";
import { formatDate } from "../lib/format";
import {
  type DraftBox,
  type LiveBreakDraft,
  liveBreakDraftIssues,
  liveBreakDraftOf,
  liveBreakDraftPatch,
  liveBreakEditScope,
  liveBreakSettingsKey,
  type RefusedLiveBreakBoxes,
  refusedLiveBreakBoxesOf,
  scopeLiveBreakPatch,
} from "../lib/liveBreakDraft";
import {
  LIVE_BREAK_BOX_INELIGIBLE_LABELS,
  LIVE_BREAK_CLOSE_REASON_LABELS,
  LIVE_BREAK_QUEUE_TIER_LABELS,
  LIVE_BREAK_RELEASE_REASON_LABELS,
  LIVE_BREAK_STATE_BADGE,
  LIVE_BREAK_STATE_LABELS,
  liveBreakState,
  staffEndUserLabel,
} from "../lib/liveBreakStaff";
import { ratePercentText } from "../lib/liveBreaks";
import { useStorefrontConfigQuery } from "../lib/storefront";
import { primaryStorefrontHostname, storefrontUrl } from "../lib/storefrontUrl";
import { useTRPC } from "../lib/trpc";
import { useTenantRole } from "../lib/useTenantRole";
import { useUnsavedChangesGuard } from "../lib/useUnsavedChangesGuard";

type LiveBreak = StaffLiveBreakWire;
type Entry = StaffLiveBreakEntryWire;

/** How often an open live break's page reads it again: its queue and stage move on their own. */
const OPEN_REFRESH_MS = 10_000;

/**
 * One live break (boxed's live-break room admin): what it is doing, its figures, the queue with
 * Pick and Cancel, its boxes, top pulls and settings. Admins edit it, End it and Force close it;
 * everyone else reads.
 */
export function LiveBreakPage() {
  const { tenantId, liveBreakId } = useParams({
    from: "/app/tenants/$tenantId/storefront/live-breaks/list/$liveBreakId",
  });
  const trpc = useTRPC();
  const currency = useCurrency();
  const { can } = useTenantRole();
  const canAct = can("admin");
  const detail = useQuery({
    ...trpc.liveBreaks.get.queryOptions({ liveBreakId }),
    refetchInterval: (query) =>
      query.state.data?.liveBreak.status === "open" ? OPEN_REFRESH_MS : false,
  });
  const data = detail.data;

  if (detail.error) return <ErrorAlert message={detail.error.message} />;
  if (!data) return <Skeleton className="h-96" />;
  const liveBreak = data.liveBreak;
  const open = liveBreak.status === "open";

  return (
    <div className="flex flex-col gap-6">
      <LiveBreakHeader tenantId={tenantId} liveBreak={liveBreak} canAct={canAct} />
      <LiveBreakFigures
        liveBreak={liveBreak}
        stats={data.stats}
        currency={currency}
        // The moment the figures were read, so Length moves with each refetch while open.
        now={new Date(detail.dataUpdatedAt)}
      />

      <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_26rem]">
        <div className="flex min-w-0 flex-col gap-6">
          {data.presentingEntry && <OnStageCard entry={data.presentingEntry} />}
          <QueueCard tenantId={tenantId} liveBreak={liveBreak} canAct={canAct && open} />
          <TopPullsCard tenantId={tenantId} detail={data} />
          <BoxesCard detail={data} />
        </div>
        <div className="flex min-w-0 flex-col gap-6">
          <SettingsCard
            // A setting another writer changed resets the draft; the stage's writes do not.
            key={liveBreakSettingsKey(
              liveBreak,
              data.boxes.map((box) => box.boxId),
            )}
            detail={data}
            canEdit={canAct && open}
          />
          <StateFacts detail={data} />
        </div>
      </div>
    </div>
  );
}

function LiveBreakHeader({
  tenantId,
  liveBreak,
  canAct,
}: {
  tenantId: string;
  liveBreak: LiveBreak;
  canAct: boolean;
}) {
  const storefront = useStorefrontConfigQuery().query.data;
  const hostname = storefront ? primaryStorefrontHostname(storefront.domains) : null;
  const state = liveBreakState(liveBreak);
  return (
    <header className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
      <div className="flex min-w-0 flex-col gap-2">
        <h1 className="text-xl font-semibold tracking-tight break-words">{liveBreak.title}</h1>
        <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          <Badge variant={LIVE_BREAK_STATE_BADGE[state]}>{LIVE_BREAK_STATE_LABELS[state]}</Badge>
          {liveBreak.host ? (
            <span>
              Hosted by{" "}
              <Link
                to="/tenants/$tenantId/storefront/live-breaks/hosts/$userId"
                params={{ tenantId, userId: liveBreak.host.endUserId }}
                className="text-foreground hover:underline"
              >
                {staffEndUserLabel(liveBreak.host)}
              </Link>
            </span>
          ) : (
            <Badge variant="secondary">House live break</Badge>
          )}
          <Badge variant="outline">{liveBreak.isPublic ? "Listed" : "Private"}</Badge>
          {liveBreak.isAutoMode && <Badge variant="outline">Auto Mode</Badge>}
          {liveBreak.closeReason && (
            <span>{LIVE_BREAK_CLOSE_REASON_LABELS[liveBreak.closeReason]}</span>
          )}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {hostname && (
          <a
            href={`${storefrontUrl(hostname)}${liveBreakStorefrontPath(liveBreak.id)}`}
            target="_blank"
            rel="noreferrer"
            className={buttonVariants({ variant: "outline", size: "sm" })}
          >
            View on storefront
            <ExternalLink />
          </a>
        )}
        {canAct && liveBreak.status === "open" && !liveBreak.isDraining && (
          <EndButton liveBreak={liveBreak} />
        )}
        {canAct && liveBreak.status === "open" && <ForceCloseButton liveBreak={liveBreak} />}
      </div>
    </header>
  );
}

/** Refreshes every live-break read after a write. */
function useInvalidateLiveBreaks() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: trpc.liveBreaks.pathKey() });
    void queryClient.invalidateQueries({ queryKey: trpc.users.liveBreaks.pathKey() });
  };
}

/** End (`liveBreaks.end`, admins): Draining now, then closed once the queue is empty. */
function EndButton({ liveBreak }: { liveBreak: LiveBreak }) {
  const trpc = useTRPC();
  const invalidate = useInvalidateLiveBreaks();
  const [open, setOpen] = useState(false);
  const end = useMutation(
    trpc.liveBreaks.end.mutationOptions({
      onSuccess: ({ ended, liveBreak: after }) => {
        toast.success(
          !ended
            ? `${liveBreak.title} was already ending.`
            : after.status === "closed"
              ? `${liveBreak.title} is closed: nothing was queued.`
              : `${liveBreak.title} is Draining: it closes once its queue has opened.`,
        );
        invalidate();
        setOpen(false);
      },
    }),
  );
  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger render={<Button type="button" variant="outline" size="sm" />}>
        End
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{`End ${liveBreak.title}?`}</AlertDialogTitle>
          <AlertDialogDescription>
            It stops taking purchases now and turns Auto Mode on, so every queue entry already
            bought still opens; it closes once the queue is empty. Queued buyers may still leave.
            {liveBreak.host ? " Its host is notified." : ""}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={end.isPending}
            onClick={() => end.mutate({ liveBreakId: liveBreak.id })}
          >
            {end.isPending ? "Ending…" : "End live break"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/** Force close (`liveBreaks.forceClose`, admins): closed at once, whatever is on stage. */
function ForceCloseButton({ liveBreak }: { liveBreak: LiveBreak }) {
  const trpc = useTRPC();
  const invalidate = useInvalidateLiveBreaks();
  const [open, setOpen] = useState(false);
  const forceClose = useMutation(
    trpc.liveBreaks.forceClose.mutationOptions({
      onSuccess: ({ applied, releasedEntryIds, tailComplete }) => {
        const released = releasedEntryIds.length;
        toast.success(
          applied
            ? `${liveBreak.title} is closed${released > 0 ? `; ${released} queued ${released === 1 ? "entry" : "entries"} released` : ""}.${tailComplete ? "" : " Claiming its reveals finishes in the background."}`
            : `${liveBreak.title} was already closed.`,
        );
        invalidate();
        setOpen(false);
      },
    }),
  );
  const queued = liveBreak.queuedCount;
  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger render={<Button type="button" variant="destructive" size="sm" />}>
        Force close
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{`Force close ${liveBreak.title}?`}</AlertDialogTitle>
          <AlertDialogDescription>
            {`It closes now, whatever is on stage. ${
              queued > 0
                ? `Its ${queued} queued ${queued === 1 ? "entry is" : "entries are"} released, and each buyer's hold comes back to their balance.`
                : "Nothing is queued."
            } Reveals already rolled are claimed for their buyers. This can't be undone.`}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            disabled={forceClose.isPending}
            onClick={() => forceClose.mutate({ liveBreakId: liveBreak.id })}
          >
            {forceClose.isPending ? "Closing…" : "Force close"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/** The entry presenting or revealed now, with what it rolled once revealed. */
function OnStageCard({ entry }: { entry: Entry }) {
  const currency = useCurrency();
  return (
    <Card size="sm">
      <CardHeader>
        <CardTitle>On stage</CardTitle>
        <CardDescription>
          {`${staffEndUserLabel(entry.buyer)} · ${entry.amount} × ${entry.boxName} at ${formatAmount(entry.priceCents, currency)}`}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {entry.opens === null ? (
          <p className="text-sm text-muted-foreground">Presenting: nothing is rolled yet.</p>
        ) : (
          <ul className="grid gap-2 sm:grid-cols-2">
            {entry.opens.map((open) => (
              <li key={open.openId} className="flex items-center gap-3 rounded-md border p-2">
                <BoxImage
                  src={open.item.imageUrl}
                  className="size-10 shrink-0 rounded object-contain"
                />
                <span className="min-w-0 flex-1 truncate text-sm">{open.item.name}</span>
                <span className="shrink-0 font-mono text-xs">
                  {formatAmount(open.item.valueCents, currency)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

// ---- the queue ---------------------------------------------------------------------------------

const QUEUE_VIEWS = { queued: "Queued", all: "Every entry" } as const;
type QueueView = keyof typeof QUEUE_VIEWS;

const entryColumns = createDataTableColumns<Entry>();
const ENTRY_PINNED = { start: [], end: ["actions"] };

function placeText(entry: Entry): string {
  if (entry.isOnStage) return "On stage";
  if (entry.status === "opened") return "Opened";
  if (entry.status === "released") return "Released";
  if (entry.position === 1) return "Up Next";
  return entry.position === null ? "—" : `#${entry.position}`;
}

function buildEntryColumns(args: {
  tenantId: string;
  currency: Currency;
  liveBreakId: string;
  canAct: boolean;
}) {
  const money = (value: unknown) => amountInputValue(Number(value), args.currency);
  const columns = [
    entryColumns.accessor((entry) => placeText(entry), {
      id: "place",
      header: "Place",
      size: 112,
      cell: ({ getValue, row }) => (
        <span className="flex flex-wrap items-center gap-1">
          <span className={row.original.isOnStage ? "font-medium" : undefined}>{getValue()}</span>
          {row.original.queueTier !== "standard" && (
            <Badge variant="outline">{LIVE_BREAK_QUEUE_TIER_LABELS[row.original.queueTier]}</Badge>
          )}
        </span>
      ),
      meta: { filter: "text" },
    }),
    entryColumns.accessor((entry) => staffEndUserLabel(entry.buyer), {
      id: "buyer",
      header: "Buyer",
      cell: ({ getValue, row }) => (
        <Link
          to="/tenants/$tenantId/users/$userId"
          params={{ tenantId: args.tenantId, userId: row.original.buyer.endUserId }}
          search={{ tab: "liveBreaks" }}
          className="hover:underline"
        >
          {getValue()}
        </Link>
      ),
      meta: { filter: "text" },
    }),
    entryColumns.accessor("boxName", {
      header: "Box",
      size: 200,
      cell: ({ getValue, row }) => (
        <span className="flex min-w-0 items-center gap-2">
          <BoxImage
            src={row.original.boxImageUrl}
            className="size-7 shrink-0 rounded border object-contain"
          />
          <span className="truncate" title={`${getValue()} · version ${row.original.boxVersion}`}>
            {getValue()}
          </span>
        </span>
      ),
      meta: { filter: "text" },
    }),
    entryColumns.accessor("amount", {
      header: "Boxes",
      size: 80,
      meta: { align: "end", filter: "number" },
    }),
    entryColumns.accessor("priceCents", {
      header: amountColumnHeader("Price each", args.currency),
      cell: ({ getValue }) => (
        <span className="font-mono text-xs">{formatAmount(getValue(), args.currency)}</span>
      ),
      meta: { align: "end", filter: "number", filterCents: true, csv: money },
    }),
    entryColumns.accessor("holdCents", {
      header: amountColumnHeader("Total", args.currency),
      cell: ({ getValue }) => (
        <span className="font-mono text-xs">{formatAmount(getValue(), args.currency)}</span>
      ),
      meta: { align: "end", filter: "number", filterCents: true, csv: money },
    }),
    entryColumns.accessor(
      (entry) =>
        entry.releaseReason ? LIVE_BREAK_RELEASE_REASON_LABELS[entry.releaseReason] : entry.status,
      {
        id: "status",
        header: "Status",
        cell: ({ getValue, row }) =>
          row.original.status === "queued" ? (
            <span className="text-muted-foreground">queued</span>
          ) : row.original.status === "opened" ? (
            <Badge variant="secondary">
              {row.original.openGroup?.status === "claimed" ? "opened · claimed" : "opened"}
            </Badge>
          ) : (
            <Badge variant="outline">{getValue()}</Badge>
          ),
        meta: { filter: "text" },
      },
    ),
    entryColumns.accessor("createdAt", {
      header: "Bought",
      cell: ({ getValue }) => <ShortDate value={getValue()} />,
    }),
  ];
  if (!args.canAct) return entryColumns.columns(columns);
  return entryColumns.columns([
    ...columns,
    entryColumns.display({
      id: "actions",
      size: 160,
      cell: ({ row }) =>
        row.original.status === "queued" && !row.original.isOnStage ? (
          <div className="flex justify-end gap-1">
            {row.original.position !== 1 && (
              <PickEntryButton liveBreakId={args.liveBreakId} entry={row.original} />
            )}
            <CancelEntryButton liveBreakId={args.liveBreakId} entry={row.original} />
          </div>
        ) : null,
    }),
  ]);
}

/**
 * The queue (`liveBreaks.queue`): the entries waiting in queue order, the one on stage marked, or
 * every entry newest first. Admins Pick an entry up to the front lane or cancel it while the live
 * break is open.
 */
function QueueCard({
  tenantId,
  liveBreak,
  canAct,
}: {
  tenantId: string;
  liveBreak: LiveBreak;
  canAct: boolean;
}) {
  const trpc = useTRPC();
  const currency = useCurrency();
  const [view, setView] = useState<QueueView>(liveBreak.status === "open" ? "queued" : "all");
  const queue = useInfiniteQuery({
    ...trpc.liveBreaks.queue.infiniteQueryOptions(
      { liveBreakId: liveBreak.id, view, limit: 100 },
      { getNextPageParam: (last) => last.nextCursor },
    ),
    refetchInterval: liveBreak.status === "open" ? OPEN_REFRESH_MS : false,
  });
  // A queue key only decreases, so an entry picked between two pages can arrive twice: keep one.
  const rows = useMemo(() => {
    const seen = new Set<string>();
    const out: Entry[] = [];
    for (const entry of queue.data?.pages.flatMap((page) => page.entries) ?? []) {
      if (seen.has(entry.id)) continue;
      seen.add(entry.id);
      out.push(entry);
    }
    return out;
  }, [queue.data]);
  const columns = useMemo(
    () => buildEntryColumns({ tenantId, currency, liveBreakId: liveBreak.id, canAct }),
    [tenantId, currency, liveBreak.id, canAct],
  );
  const queuedCount = queue.data?.pages[0]?.queuedCount ?? liveBreak.queuedCount;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Queue</CardTitle>
        <CardDescription>
          {`${formatNumber(queuedCount)} queued. A Pick moves an entry up behind Up Next; cancelling releases it and gives the buyer's hold back.`}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {/* Above the table rather than in its toolbar, whose trailing group never wraps. */}
        <div className="flex flex-wrap items-center gap-2">
          <Select
            items={QUEUE_VIEWS}
            value={view}
            onValueChange={(next) => next && setView(next as QueueView)}
          >
            <SelectTrigger aria-label="Which entries" className="w-36" size="sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="queued">{QUEUE_VIEWS.queued}</SelectItem>
              <SelectItem value="all">{QUEUE_VIEWS.all}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {queue.error ? (
          <ErrorAlert message={queue.error.message} />
        ) : queue.isPending ? (
          <Skeleton className="h-40" />
        ) : (
          <DataTable
            columns={columns}
            data={rows}
            getRowId={(entry) => entry.id}
            pinned={canAct ? ENTRY_PINNED : undefined}
            search="Search entries"
            csvName={`live-break-${liveBreak.id}-${view}`}
            maxHeight="36rem"
            emptyMessage={view === "queued" ? "Nothing is queued." : "No one has bought in yet."}
          />
        )}
        <LoadMoreButton
          hasNextPage={queue.hasNextPage}
          isFetchingNextPage={queue.isFetchingNextPage}
          onClick={() => void queue.fetchNextPage()}
        />
      </CardContent>
    </Card>
  );
}

/** Pick (`liveBreaks.pickEntry`, admins): into the front lane, behind Up Next. */
function PickEntryButton({ liveBreakId, entry }: { liveBreakId: string; entry: Entry }) {
  const trpc = useTRPC();
  const invalidate = useInvalidateLiveBreaks();
  const pick = useMutation(
    trpc.liveBreaks.pickEntry.mutationOptions({
      onSuccess: ({ moved }) => {
        toast.success(
          moved
            ? `${staffEndUserLabel(entry.buyer)}'s entry moved up.`
            : `${staffEndUserLabel(entry.buyer)}'s entry is already in the front lane.`,
        );
        invalidate();
      },
    }),
  );
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      disabled={pick.isPending}
      onClick={() => pick.mutate({ liveBreakId, entryId: entry.id })}
    >
      Pick
    </Button>
  );
}

/** Cancel (`liveBreaks.cancelEntry`, admins): released, the hold back to the buyer. */
function CancelEntryButton({ liveBreakId, entry }: { liveBreakId: string; entry: Entry }) {
  const trpc = useTRPC();
  const currency = useCurrency();
  const invalidate = useInvalidateLiveBreaks();
  const [open, setOpen] = useState(false);
  const cancel = useMutation(
    trpc.liveBreaks.cancelEntry.mutationOptions({
      onSuccess: () => {
        toast.success(`${staffEndUserLabel(entry.buyer)}'s entry is released.`);
        invalidate();
        setOpen(false);
      },
    }),
  );
  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger render={<Button type="button" variant="ghost" size="sm" />}>
        Cancel
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{`Cancel ${staffEndUserLabel(entry.buyer)}'s entry?`}</AlertDialogTitle>
          <AlertDialogDescription>
            {`${entry.amount} × ${entry.boxName} leaves the queue unopened, and the ${formatAmount(entry.holdCents, currency)} it holds goes back to the buyer's balance.`}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Keep it</AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            disabled={cancel.isPending}
            onClick={() => cancel.mutate({ liveBreakId, entryId: entry.id })}
          >
            {cancel.isPending ? "Cancelling…" : "Cancel entry"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ---- pulls and boxes ---------------------------------------------------------------------------

function TopPullsCard({
  tenantId,
  detail,
}: {
  tenantId: string;
  detail: StaffLiveBreakDetailWire;
}) {
  const currency = useCurrency();
  const pulls = detail.stats.topPulls;
  return (
    <Card>
      <CardHeader>
        <CardTitle>Top pulls</CardTitle>
        <CardDescription>The most valuable items its Reveals rolled.</CardDescription>
      </CardHeader>
      <CardContent>
        {pulls.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nothing has opened yet.</p>
        ) : (
          <ol className="divide-y rounded-md border">
            {pulls.map((pull) => (
              <li key={pull.openId} className="flex items-center gap-3 px-3 py-2">
                <BoxImage
                  src={pull.item.imageUrl}
                  className="size-10 shrink-0 rounded object-contain"
                />
                <div className="grid min-w-0 flex-1 gap-0.5">
                  <span className="truncate text-sm font-medium">{pull.item.name}</span>
                  <span className="truncate text-xs text-muted-foreground">
                    <Link
                      to="/tenants/$tenantId/users/$userId"
                      params={{ tenantId, userId: pull.buyer.endUserId }}
                      className="hover:underline"
                    >
                      {staffEndUserLabel(pull.buyer)}
                    </Link>
                    {` · ${pull.boxName} at ${formatAmount(pull.priceCents, currency)} · ${formatDate(pull.openedAt)}`}
                  </span>
                </div>
                <span className="shrink-0 font-mono text-sm">
                  {formatAmount(pull.item.valueCents, currency)}
                </span>
              </li>
            ))}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}

/** One row of the boxes card: a live-break box as it sells now, and what it has done. */
interface BoxRow {
  boxId: string;
  name: string;
  imageUrl: string;
  /** Null for a box opened here that is no longer one of its boxes. */
  offer: StaffLiveBreakDetailWire["boxes"][number] | null;
  viewerOpens: number;
  selfOpens: number;
  spendCents: number;
}

const boxColumns = createDataTableColumns<BoxRow>();

function buildBoxColumns(currency: Currency, isHouse: boolean) {
  const money = (value: unknown) => amountInputValue(Number(value), currency);
  return boxColumns.columns([
    boxColumns.accessor("name", {
      header: "Box",
      size: 220,
      cell: ({ getValue, row }) => (
        <span className="flex min-w-0 items-center gap-2">
          <BoxImage
            src={row.original.imageUrl}
            className="size-8 shrink-0 rounded border object-contain"
          />
          <span className="truncate">{getValue()}</span>
        </span>
      ),
      meta: { filter: "text" },
    }),
    boxColumns.accessor((row) => row.offer?.boxPriceCents ?? null, {
      id: "boxPrice",
      header: amountColumnHeader("Box price", currency),
      cell: ({ getValue }) => {
        const cents = getValue();
        return cents === null ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          <span className="font-mono text-xs">{formatAmount(cents, currency)}</span>
        );
      },
      meta: { align: "end", csv: (value) => (value === null ? "" : money(value)) },
    }),
    ...(isHouse
      ? []
      : [
          boxColumns.accessor((row) => row.offer?.priceCents ?? null, {
            id: "price",
            header: amountColumnHeader("Live-break price", currency),
            cell: ({ getValue }) => {
              const cents = getValue();
              return cents === null ? (
                <span className="text-muted-foreground">—</span>
              ) : (
                <span className="font-mono text-xs">{formatAmount(cents, currency)}</span>
              );
            },
            meta: { align: "end", csv: (value) => (value === null ? "" : money(value)) },
          }),
        ]),
    boxColumns.accessor(
      (row) =>
        row.offer === null
          ? "No longer one of its boxes"
          : row.offer.ineligibleReason
            ? LIVE_BREAK_BOX_INELIGIBLE_LABELS[row.offer.ineligibleReason]
            : "Selling",
      {
        id: "selling",
        header: "Sells",
        cell: ({ getValue, row }) =>
          row.original.offer && !row.original.offer.ineligibleReason ? (
            <span className="text-muted-foreground">{getValue()}</span>
          ) : (
            <Badge variant="outline">{getValue()}</Badge>
          ),
        meta: { filter: "text" },
      },
    ),
    boxColumns.accessor((row) => row.viewerOpens + row.selfOpens, {
      id: "opens",
      header: "Opens",
      size: 88,
      meta: { align: "end", filter: "number" },
    }),
    boxColumns.accessor("spendCents", {
      header: amountColumnHeader("Spend", currency),
      cell: ({ getValue }) => (
        <span className="font-mono text-xs">{formatAmount(getValue(), currency)}</span>
      ),
      meta: { align: "end", filter: "number", filterCents: true, csv: money },
    }),
  ]);
}

/** Its boxes as they sell now, merged with what each has opened here (and boxes since removed). */
function BoxesCard({ detail }: { detail: StaffLiveBreakDetailWire }) {
  const currency = useCurrency();
  const rows = useMemo(() => {
    const stats = new Map(detail.stats.boxes.map((box) => [box.boxId, box]));
    const out: BoxRow[] = detail.boxes.map((offer) => {
      const stat = stats.get(offer.boxId);
      return {
        boxId: offer.boxId,
        name: offer.name,
        imageUrl: offer.imageUrl,
        offer,
        viewerOpens: stat?.viewerOpens ?? 0,
        selfOpens: stat?.selfOpens ?? 0,
        spendCents: (stat?.viewerSpendCents ?? 0) + (stat?.selfSpendCents ?? 0),
      };
    });
    const listed = new Set(detail.boxes.map((offer) => offer.boxId));
    for (const stat of detail.stats.boxes) {
      if (listed.has(stat.boxId)) continue;
      out.push({
        boxId: stat.boxId,
        name: stat.boxName,
        imageUrl: stat.boxImageUrl,
        offer: null,
        viewerOpens: stat.viewerOpens,
        selfOpens: stat.selfOpens,
        spendCents: stat.viewerSpendCents + stat.selfSpendCents,
      });
    }
    return out;
  }, [detail.boxes, detail.stats.boxes]);
  const columns = useMemo(
    () => buildBoxColumns(currency, detail.liveBreak.isHouse),
    [currency, detail.liveBreak.isHouse],
  );
  return (
    <Card>
      <CardHeader>
        <CardTitle>Boxes</CardTitle>
        <CardDescription>
          {detail.liveBreak.isRestrictedToLiveBreakBoxes
            ? "It sells only these boxes."
            : "Its own boxes, and any other box opened here: it sells any eligible box at or above its minimum price."}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <DataTable
          columns={columns}
          data={rows}
          getRowId={(row) => row.boxId}
          search={false}
          csvName={`live-break-${detail.liveBreak.id}-boxes`}
          emptyMessage="No boxes."
        />
      </CardContent>
    </Card>
  );
}

// ---- settings and state ------------------------------------------------------------------------

function draftBoxesOf(detail: StaffLiveBreakDetailWire): DraftBox[] {
  return detail.boxes.map((box) => ({
    id: box.boxId,
    name: box.name,
    imageUrl: box.imageUrl,
    priceCents: box.boxPriceCents,
  }));
}

const NO_REFUSED_BOXES: RefusedLiveBreakBoxes = new Map();

/**
 * The staff edit (`liveBreaks.update`, admins): a draft until saved, and the save sends only the
 * settings that changed. Read-only to everyone else, once the live break has closed and while a
 * hosted one Drains; a house one Draining takes its end date alone (the drain's undo). Leaving
 * with unsaved changes asks first, and a box the save was refused for is marked in the list.
 */
export function SettingsCard({
  detail,
  canEdit,
}: {
  detail: StaffLiveBreakDetailWire;
  canEdit: boolean;
}) {
  const trpc = useTRPC();
  const currency = useCurrency();
  const invalidate = useInvalidateLiveBreaks();
  const { liveBreak } = detail;
  const saved = useMemo(
    () => liveBreakDraftOf(liveBreak, draftBoxesOf(detail), currency),
    [liveBreak, detail, currency],
  );
  const [draft, setDraft] = useState<LiveBreakDraft | null>(null);
  const [refusedBoxes, setRefusedBoxes] = useState<RefusedLiveBreakBoxes>(NO_REFUSED_BOXES);
  const current = draft ?? saved;
  const context = {
    currency,
    isHouse: liveBreak.isHouse,
    maxRateBps: detail.maxRateBps,
    now: new Date(),
  };
  const scope = liveBreakEditScope(liveBreak, canEdit);
  const locked = scope === "none";
  const issues = liveBreakDraftIssues(current, context, saved);
  const patch = scopeLiveBreakPatch(
    draft ? liveBreakDraftPatch(current, saved, context) : {},
    scope,
  );
  const dirty = patch === null || Object.keys(patch).length > 0;
  const saveable = patch !== null && Object.keys(patch).length > 0;
  const update = useMutation(
    trpc.liveBreaks.update.mutationOptions({
      onSuccess: () => {
        toast.success(`${liveBreak.title} saved.`);
        setDraft(null);
        setRefusedBoxes(NO_REFUSED_BOXES);
        invalidate();
      },
      // The toast names the refusal; the list marks which boxes it was for.
      onError: (error) => setRefusedBoxes(refusedLiveBreakBoxesOf(error)),
    }),
  );
  const save = () => update.mutateAsync({ liveBreakId: liveBreak.id, ...patch });
  const discard = () => {
    setDraft(null);
    setRefusedBoxes(NO_REFUSED_BOXES);
  };
  const guard = useUnsavedChangesGuard({
    dirty: !locked && dirty,
    // Save and leave only for a draft that saves; one with an issue can only be kept or dropped.
    onSave: saveable ? save : undefined,
    onDiscard: discard,
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Settings</CardTitle>
        <CardDescription>
          {liveBreak.status === "closed"
            ? "Closed: these are the settings it ended with."
            : liveBreak.isDraining
              ? liveBreak.isHouse
                ? "Draining: only its end can change. Moving it later or clearing it undoes the drain."
                : "Draining: it takes no more changes."
              : canEdit
                ? "Changes apply as soon as they're saved; queued entries keep the price and rate they were bought at."
                : "Only admins can change a live break."}
        </CardDescription>
        {!locked && draft !== null && (
          <CardAction>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={update.isPending}
              onClick={discard}
            >
              Discard
            </Button>
          </CardAction>
        )}
      </CardHeader>
      <CardContent>
        <UnsavedChangesDialog guard={guard} />
        <form
          className="grid gap-6"
          onSubmit={(e) => {
            e.preventDefault();
            if (saveable) update.mutate({ liveBreakId: liveBreak.id, ...patch });
          }}
        >
          <LiveBreakSettingsFields
            idPrefix={`live-break-${liveBreak.id}`}
            mode="edit"
            isHouse={liveBreak.isHouse}
            draft={current}
            issues={draft ? issues : {}}
            maxRateBps={detail.maxRateBps}
            disabled={locked || update.isPending}
            endsAtOnly={scope === "endsAt"}
            refusedBoxes={refusedBoxes}
            onChange={(change) => setDraft({ ...current, ...change })}
          />
          {!locked && (
            <div className="flex gap-2">
              <Button type="submit" disabled={!saveable || update.isPending}>
                {update.isPending ? "Saving…" : "Save"}
              </Button>
            </div>
          )}
        </form>
      </CardContent>
    </Card>
  );
}

/** What the stage and the host are doing, and how it ended. */
function StateFacts({ detail }: { detail: StaffLiveBreakDetailWire }) {
  const { liveBreak, twitchStatus } = detail;
  const currency = useCurrency();
  return (
    <FactGroup title="State">
      <FactList>
        <Fact label="Stage">
          {liveBreak.revealPhase === "idle"
            ? "Idle"
            : liveBreak.revealPhase === "presenting"
              ? "Presenting"
              : "Revealed"}
        </Fact>
        {!liveBreak.isHouse && (
          <Fact label="Host">
            {liveBreak.isHostPresent ? "Present" : "Away"} · seen{" "}
            {formatDate(liveBreak.hostLastSeenAt)}
          </Fact>
        )}
        <Fact label="Rate">
          {liveBreak.commissionRateBps === null
            ? "None (house)"
            : `${ratePercentText(liveBreak.commissionRateBps)}%`}
        </Fact>
        <Fact label="Lowest price">
          {liveBreak.lowestPriceCents === null
            ? "Nothing can open"
            : formatAmount(liveBreak.lowestPriceCents, currency)}
        </Fact>
        <Fact label="Twitch">
          {liveBreak.twitchChannel ? (
            <a
              href={`https://www.twitch.tv/${liveBreak.twitchChannel}`}
              target="_blank"
              rel="noreferrer"
              className="hover:underline"
            >
              {liveBreak.twitchChannel}
              {twitchStatus?.isLive ? " · live" : ""}
            </a>
          ) : (
            "None"
          )}
        </Fact>
        <Fact label="Started">{formatDate(liveBreak.createdAt)}</Fact>
        {liveBreak.endsAt && (
          <Fact label={liveBreak.isDraining ? "Draining since" : "Ends"}>
            {formatDate(liveBreak.endsAt)}
            {liveBreak.endedBy ? ` · ended by ${liveBreak.endedBy}` : ""}
          </Fact>
        )}
        {liveBreak.closedAt && <Fact label="Closed">{formatDate(liveBreak.closedAt)}</Fact>}
        <Fact label="Id" mono>
          {liveBreak.id}
        </Fact>
      </FactList>
    </FactGroup>
  );
}
